import { useCallback } from "react";
import LOCAL_TABLE_CONFIG_KEY from "../../types/TableLocalConfigKeys";
import {
    addNewColumn,
    onColumnReorder,
    onColumnToggle,
    removeCustomColumn
} from "../Shared/TableFunctions";
import { DefaultHeader } from "../Shared/DefaultHeader";
import { MultiSelectChangeEvent } from "primereact/multiselect";
import { SharedTableColumnConfig } from "../../types/SharedTableColumnConfig";
import { HeaderType } from "../../types/HeaderType";
import { RemoveCustomColumnParams } from "../../types/RemoveColumnProps";
import { USER_APP_STATE_HOME_TABLE_KEY } from "../../utils/Constants";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectUserDetails } from "../../store/selectors/userSelectors";
import { selectDataTableFilterData } from "../../store/selectors/filtersSelectors";
import { selectFleet } from "../../store/selectors/fleetSelectors";
import { updateGroupData } from "../../store/thunks/groupThunks";

type Props = {
    columns: SharedTableColumnConfig[];
    setColumns: React.Dispatch<React.SetStateAction<SharedTableColumnConfig[]>>;
    selectedColumns: SharedTableColumnConfig[];
    setSelectedColumns: React.Dispatch<
        React.SetStateAction<SharedTableColumnConfig[]>
    >;
};

export const FleetTableHeader = (props: Props) => {
    const { columns, setColumns, selectedColumns, setSelectedColumns } = props;
    const dispatch = useAppDispatch();

    const { loading } = useAppSelector(selectFleet);
    const userDetails = useAppSelector(selectUserDetails);
    const { table: filters } = useAppSelector(selectDataTableFilterData);

    const onComplete = (data: Record<string, unknown>) => {
        dispatch(updateGroupData(data));
    };

    const onAddNewColumn = useCallback(
        (
            newColumns: SharedTableColumnConfig[],
            selected: SharedTableColumnConfig[]
        ) => {
            setColumns(newColumns);
            setSelectedColumns(selected);
        },
        []
    );

    const handleOnColumnReorder = useCallback(
        (orderedColumns: any) =>
            onColumnReorder(
                orderedColumns,
                LOCAL_TABLE_CONFIG_KEY.FLEET_COLUMNS,
                LOCAL_TABLE_CONFIG_KEY.FLEET_ORDERED_COLUMNS,
                columns,
                selectedColumns,
                setColumns,
                setSelectedColumns,
                userDetails,
                USER_APP_STATE_HOME_TABLE_KEY
            ),
        [columns, selectedColumns, userDetails]
    );

    const handleOnColumnToggle = useCallback(
        (event: MultiSelectChangeEvent) =>
            onColumnToggle(
                event,
                LOCAL_TABLE_CONFIG_KEY.FLEET_COLUMNS,
                columns,
                setSelectedColumns,
                userDetails,
                USER_APP_STATE_HOME_TABLE_KEY
            ),
        [columns, userDetails]
    );

    const handleOnAddNewColumn = useCallback(
        (columnName: string, dataType: string) =>
            addNewColumn(
                columnName,
                dataType,
                selectedColumns,
                userDetails,
                onAddNewColumn,
                USER_APP_STATE_HOME_TABLE_KEY,
                columns,
                onComplete
            ),
        [selectedColumns, userDetails, columns]
    );

    const handleOnRemoveColumn = useCallback(
        (columnName: string) =>
            removeCustomColumn(
                {
                    columnName,
                    columns,
                    selectedColumns,
                    setColumns,
                    setSelectedColumns,
                    userDetails,
                    localStorageSelectedColumnsKey:
                        LOCAL_TABLE_CONFIG_KEY.FLEET_COLUMNS,
                    tableKey: USER_APP_STATE_HOME_TABLE_KEY
                },
                onComplete
            ),
        [columns, selectedColumns, userDetails]
    );

    return (
        <DefaultHeader
            onColumnToggle={handleOnColumnToggle}
            columns={columns}
            disabled={loading}
            headerType={HeaderType.TABLE}
            selectedColumns={selectedColumns}
            filters={filters}
            onColumnReorder={handleOnColumnReorder}
            onAddColumn={handleOnAddNewColumn}
            onRemoveColumn={handleOnRemoveColumn}
        />
    );
};
